import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { apiUrl } from 'src/config';
import { FileType, PVODataType, UploadStatusEnum, StepsEnum } from 'src/types';
import { useDataContext } from 'src/contexts';
import { safeLocalStorage } from 'src/utils/local-storage';

type FetchPvoDataOptions = {
  redirectOnError?: boolean;
};

const MEDIA_CACHE_KEY_PREFIX = 'media--';

const hasFiles = (d: PVODataType | null): boolean =>
  Boolean(d && d.files && d.files.length > 0);

const getBlobFile = async (data: PVODataType) => {
  const file = data.files?.[0];
  if (!file) return;

  const response = await fetch(file.url);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch file: ${file.url} (status: ${response.status})`
    );
  }
  const blob = await response.blob();
  return new File([blob], file.name, { type: blob.type });
};

const getFormattedData = async (pvoData: PVODataType) => {
  const blobFile = await getBlobFile(pvoData);
  const filesWithBlob: FileType[] = pvoData.files.map((data: FileType) => ({
    ...data,
    blobFile,
  }));
  return { ...pvoData, files: filesWithBlob };
};

export const useFetchPvoData = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const setPvoData = useDataContext((ctx) => ctx.setPvoData);
  const setPlaywallId = useDataContext((ctx) => ctx.setPlaywallId);
  const setActiveStep = useDataContext((ctx) => ctx.setActiveStep);
  const [isLoading, setIsLoading] = useState(false);

  const hydrateFromCache = useCallback(
    async (cachedJson: string) => {
      if (!hash) return false;

      const pvoData: PVODataType = JSON.parse(cachedJson);
      if (!hasFiles(pvoData)) return false;

      try {
        const data = await getFormattedData(pvoData);
        setPvoData(data);
        if (data.playwall_id) setPlaywallId(data.playwall_id);
        return true;
      } catch (error) {
        console.error('Failed to hydrate cached PVO data:', error);
        return false;
      }
    },
    [hash, setPvoData, setPlaywallId]
  );

  const fetchPvoData = useCallback(
    async ({ redirectOnError = false }: FetchPvoDataOptions = {}) => {
      if (!hash) return;

      setIsLoading(true);
      try {
        const cacheKey = `${MEDIA_CACHE_KEY_PREFIX}${hash}`;
        const cached = safeLocalStorage.getItem(cacheKey);
        if (cached && (await hydrateFromCache(cached))) {
          return;
        }

        const response = await fetch(`${apiUrl}/v2/pvo/${hash}`);
        if (!response.ok) return;

        const pvoData: PVODataType = await response.json();
        const status = pvoData.upload_status;

        if (status === UploadStatusEnum.ERROR) {
          navigate('/error');
          return;
        }
        if (status === UploadStatusEnum.PENDING) {
          setActiveStep(StepsEnum.CAPTURED);
          return;
        }
        if (status === UploadStatusEnum.SUCCESS && hasFiles(pvoData)) {
          const data = await getFormattedData(pvoData);

          setPvoData(data);
          if (data.playwall_id) setPlaywallId(data.playwall_id);
          safeLocalStorage.setItem(cacheKey, JSON.stringify(data));
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        if (redirectOnError) navigate('/error');
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      hash,
      navigate,
      hydrateFromCache,
      setPvoData,
      setPlaywallId,
      setActiveStep,
    ]
  );

  return { fetchPvoData, isLoading };
};
